import { useEffect, useState } from "react";
import { captureError, type ErrorContext } from "../../utils/errorReporting";
import { deriveRoute } from "../../utils/errorReporting.helpers";
import type { PendingRead } from "../../utils/pendingReads";

/**
 * Time-based escalation for `LoadingSplash` (#1770): a user on a bad
 * connection must never stare at an unexplained spinner. Pure timers — no
 * network probes, no firebase import, so it unit-tests with fake timers.
 */

export type SplashPhase = "quiet" | "still-connecting" | "stalled";

export const SPLASH_STILL_CONNECTING_MS = 5000;
export const SPLASH_STALLED_MS = 15000;

export const splashPhaseAt = (elapsedMs: number): SplashPhase => {
  if (elapsedMs >= SPLASH_STALLED_MS) return "stalled";
  if (elapsedMs >= SPLASH_STILL_CONNECTING_MS) return "still-connecting";
  return "quiet";
};

export const useSplashPhase = (): SplashPhase => {
  const [phase, setPhase] = useState<SplashPhase>("quiet");
  useEffect(() => {
    const stillConnecting = window.setTimeout(
      () => setPhase("still-connecting"),
      SPLASH_STILL_CONNECTING_MS,
    );
    const stalled = window.setTimeout(
      () => setPhase("stalled"),
      SPLASH_STALLED_MS,
    );
    return () => {
      window.clearTimeout(stillConnecting);
      window.clearTimeout(stalled);
    };
  }, []);
  return phase;
};

/**
 * Which cold-start gate a splash is waiting on (#1491): the lazy route chunk,
 * the single-segment namespace lookup, or the org config load. Tagged on the
 * stall report so the next field incident says WHERE it hung.
 */
export type SplashStage = "code" | "namespace" | "org-config";

export type StallReport = {
  stage?: SplashStage;
  pathname: string;
  online: boolean;
  /** Reads still in flight at the stall, oldest first (#1129). */
  pendingReads?: PendingRead[];
};

const formatRead = ({ label, ageMs }: PendingRead) => `${label} (${ageMs}ms)`;

/**
 * A splash that reaches `stalled` is a cold start that never settled — no
 * exception exists, so without this nothing reaches Sentry and the incident is
 * undiagnosable (#1491). Reports at most once per page load: the splash
 * remounts as the load moves between gates, and one stall is one event.
 */
export const createStallReporter = (
  capture: (error: unknown, context: ErrorContext) => void,
) => {
  let reported = false;
  return ({ stage, pathname, online, pendingReads }: StallReport): void => {
    if (reported) return;
    reported = true;
    const error = new Error(
      `Cold start stalled: still loading after ${SPLASH_STALLED_MS / 1000}s`,
    );
    error.name = "ColdStartStall";
    // Which read hung (#1129). "none" is itself a finding: nothing is waiting
    // on the network, so the stall is in the load logic, not a hung read.
    const reads = pendingReads?.map(formatRead);
    if (reads) {
      console.warn(
        `[cold-start-stall] ${stage ?? "unknown"} stage; pending reads: ${
          reads.join(", ") || "none"
        }`,
      );
    }
    capture(error, {
      failure: "cold-start-stall",
      loadStage: stage,
      route: deriveRoute(pathname),
      online,
      ...(pendingReads && {
        stalledOn: pendingReads[0]?.label ?? "none",
        pendingReads: reads,
      }),
    });
  };
};

export const reportSplashStall = createStallReporter(captureError);
